import { useSetBodyBgColor } from "@web/hooks/useSetBodyBgColor";
import { useSetBodyOverflowX } from "@web/hooks/useSetBodyOverflowX";
import { AboutParagraph } from "@web/pages/about/AboutParagraph";
import { ColorValue } from "ui";
import { AboutContainer } from "@web/pages/about/AboutContainer";
import styles from "./css/SpacedRepetitionPage.module.css";
import { Footer } from "@web/components/footers/Footer";
import { BlogHeader } from "@web/pages/blog/BlogHeader";

function Bold({ children }: { children: string }) {
  return <span style={{ fontWeight: 600 }}>{children}</span>;
}

function EbbinghausDiagrams() {
  return (
    <div>
      <div className={styles.diagrams}>
        <div className={styles.diagramContainer}>
          <div className={styles.diagramTitle}>
            Forgetting curve <b>without</b> spaced repetition
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className={styles.diagram}
            src="/images/about/ebbinghaus1.svg"
            alt="Ebbinghaus Curve"
          />
        </div>
        <div className={styles.diagramContainer}>
          <div className={styles.diagramTitle}>
            Forgetting curve <b style={{ color: ColorValue.Highlight }}>with</b>{" "}
            spaced repetition
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className={styles.diagram}
            src="/images/about/ebbinghaus2.svg"
            alt="Ebbinghaus Curve"
          />
        </div>
      </div>
      <div className={styles.diagramFooter}>
        The Ebbinghaus forgetting curve (first chart) shows that people forget
        most of what they learn in just a few days. The steepness of the curve
        represents memory decay. But with spaced repetition (second chart), the
        rate of decay will slow after each time you review a concept—meaning
        that you&apos;ll remember the concept for longer. The time between each
        review increases and makes your studying more efficient.
      </div>
    </div>
  );
}

function Title() {
  return <div className={styles.title}>A Primer on Spaced Repetition</div>;
}

function OtherResources() {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>Other Resources</div>
      <AboutParagraph>
        If you want to learn even more about spaced repetition, here are some
        resources we recommend:
        <ul>
          <li>
            <a
              className={styles.link}
              href="https://www.gwern.net/Spaced-repetition#subjects"
            >
              Spaced Repetition for Efficient Learning
            </a>
          </li>
          <li>
            <a
              className={styles.link}
              href="http://augmentingcognition.com/ltm.html"
            >
              Augmenting Long-term Memory
            </a>{" "}
          </li>
          <li>
            <a
              className={styles.link}
              href="https://numinous.productions/ttft/"
            >
              How can we develop transformative tools for thought?
            </a>{" "}
          </li>
        </ul>
      </AboutParagraph>
    </div>
  );
}

function WhatCanYouUseItFor() {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>What can you use it for?</div>
      <AboutParagraph>
        “Memorization” has a bad stigma for many people—maybe it&apos;s
        associated with cramming for the SATs or GREs, and dissociated from more
        important things like learning and understanding. However, it turns out
        that memory plays a key role in learning any subject. This may be
        unintuitive at first. It makes sense that spaced repetition might be
        helpful for learning a language or studying for exams. But what about
        learning computer science, or math, or chemistry? Even for these more
        conceptually deep subjects, spaced repetition is still useful—spaced
        repetition isn&apos;t just about rote memorization. It helps you
        memorize key details and concepts which form the foundation for in-depth
        understanding and mastery. Just as it&apos;s hard to speak a language if
        you don&apos;t remember many words, it&apos;s hard to understand a
        subject if you&apos;re constantly forgetting the fundamentals.
      </AboutParagraph>
      <AboutParagraph>
        Here are some uses of spaced repetition that we find particularly
        valuable:
        <ol>
          <li>Learning a language</li>
          <li>Studying for exams like the SAT and GRE</li>
          <li>Learning a new computer science language or API</li>
          <li>
            Remembering the most important concepts from non-fiction books
          </li>
          <li>Learning about a complex topic like quantum physics</li>
        </ol>
      </AboutParagraph>
    </div>
  );
}

function WhatIsSpacedRepetition() {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>What is spaced repetition?</div>
      <AboutParagraph>
        Simply put, <Bold>spaced repetition</Bold> is a studying technique that
        lets you memorize things more efficiently and effectively. For example,
        let&apos;s say you want to memorize the definition of “sangfroid” for
        the rest of your life. There are a few possible approaches you might
        come up with.
        <ol>
          <li>
            Review the definition 20 or 30 times in a single day, until
            you&apos;re sure you remember it.
          </li>
          <li>
            Review the definition once a week for the rest of your life, to
            ensure you remember it even after a few years.
          </li>
          <li>
            Review the definition on a spaced repetition schedule, e.g. review
            it on day 1, then day 4, then day 11, then day 25, then day 60, etc.
            With spaced repetition, each time you successfully remember, the
            next review gets increasingly spaced out.
          </li>
        </ol>
      </AboutParagraph>
      <AboutParagraph>
        There have been a{" "}
        <a
          className={styles.link}
          href="https://www.gwern.net/Spaced-repetition"
        >
          bunch of studies
        </a>{" "}
        about the efficacy of these approaches. The basic summary is as follows.
        With the first approach, you&apos;ll remember what “sangfroid” means in
        the short-term, but will most likely forget it after a few days or
        weeks. The second and third approaches are similarly effective, but the
        third is much more efficient.{" "}
        <Bold>
          Over a span of 20 years, the second approach will take up ~2 hours of
          your life, while the third approach will take only ~5 minutes.
        </Bold>
      </AboutParagraph>
    </div>
  );
}

function WhyDoesItWork() {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>Why does it work?</div>
      <AboutParagraph>
        There are two learning strategies that explain why spaced repetition
        works. The first is called <i>active recall</i>, and the second is
        called the <i>spacing effect</i>. Let&apos;s cover them one at a time.
      </AboutParagraph>
      <AboutParagraph>
        <i>Active recall</i> is basically a fancy word for testing your
        knowledge. Whenever you take a test, or try to remember what some word
        means, you&apos;re doing active recall. The idea is that actively
        recalling some idea, fact, or concept helps you remember it. This
        contrasts with “passive review,” which is much less effective. Examples
        of passive review include reading a textbook or watching a documentary,
        assuming you&apos;re not quizzing yourself while you read or watch. Have
        you ever read a really interesting non-fiction book only to forget it
        all a few months later? That&apos;s passive review at play—if you really
        want to remember what you read, it&apos;s much more effective to quiz
        yourself on the material than to continually re-read it. For a more
        in-depth explanation of active recall, check out our{" "}
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a className={styles.link} href="/blog/active-recall">
          blog post
        </a>{" "}
        about it.
      </AboutParagraph>
      <AboutParagraph>
        The <i>spacing effect</i> says that if you want to remember something,
        it&apos;s better to space out your reviews than to cram and do a bunch
        of reviews in a single day. For example, let&apos;s say you have ten
        reviews to spend. Instead of reviewing ten times the first day,
        you&apos;re better off using one the first day, then another a day
        later, etc. Further, there have been studies that show that a specific
        type of spacing is the most efficient. Specifically, instead of
        reviewing at fixed intervals (example 2 above), you should review at
        increasingly spaced out intervals (example 3 above)—the latter is just
        as effective as the former, but far more efficient.
      </AboutParagraph>
      <EbbinghausDiagrams />
      <AboutParagraph className={styles.paragraphAfterDiagrams}>
        Now back to spaced repetition. Spaced repetition is really just a
        combination of active recall and the spacing effect! Put another way,
        it&apos;s just the practice of actively recalling something on a spaced
        schedule.
      </AboutParagraph>
    </div>
  );
}

export function SpacedRepetitionPage() {
  // Otherwise, the 100vw will cause there to be a small overflow-x
  // to see beneath the vertical scroll bar.
  useSetBodyOverflowX("hidden");
  useSetBodyBgColor(ColorValue.WebsiteBackground);

  return (
    <AboutContainer>
      <BlogHeader />
      <div className={styles.body}>
        <Title />
        <WhatIsSpacedRepetition />
        <WhyDoesItWork />
        <WhatCanYouUseItFor />
        <OtherResources />
      </div>
      <div style={{ width: "100vw" }}>
        <Footer />
      </div>
    </AboutContainer>
  );
}
